import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { DollarSign, TrendingUp, Trophy, Percent, Ticket } from 'lucide-react';

const StatCard = ({ icon: Icon, title, value, subtitle, className }) => (
  <Card className={className}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 bg-accent/10 rounded-lg">
          <Icon className="w-5 h-5 text-accent" />
        </div>
      </div>
      <div className="text-sm text-muted-foreground mb-1">{title}</div>
      <div className="text-2xl font-semibold font-mono">{value}</div>
      {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
    </CardContent>
  </Card>
);

export const TicketDetailDrawer = ({ open, onOpenChange, ticket }) => {
  if (!ticket) return null;
  
  // Use prizes_remaining_percent from Game model (or prizes_remaining from RankingResponse)
  const prizesRemaining = ticket.prizes_remaining_percent ?? ticket.prizes_remaining ?? 0;
  const depletionPercent = 100 - prizesRemaining;
  
  // Sort prize tiers by amount (descending)
  const sortedPrizeTiers = [...(ticket.prize_tiers || [])].sort((a, b) => b.prize_amount - a.prize_amount);
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="ticket-detail">
        <SheetHeader className="mb-6">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-2xl mb-2">{ticket.name}</SheetTitle>
              <SheetDescription className="flex items-center gap-2 flex-wrap">
                <span className="font-mono">Game #{ticket.game_id}</span>
                <Separator orientation="vertical" className="h-4" />
                <Badge variant="outline">${ticket.price} Ticket</Badge>
                <Badge variant={ticket.status === 'Active' ? 'default' : 'destructive'}>
                  {ticket.status}
                </Badge>
                {ticket.jackpot_available && (
                  <Badge variant="warning" className="bg-warning/20 text-warning">
                    <Trophy className="w-3 h-3 mr-1" /> Jackpot Available
                  </Badge>
                )}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>
        
        <div className="space-y-6">
          {/* Key Metrics with Ticket Image */}
          <div className="grid grid-cols-3 gap-4">
            {/* Ticket Image - spans 2 rows */}
            <div className="row-span-2 flex items-center justify-center">
              <div className="w-full max-w-[180px] aspect-[3/4] rounded-xl overflow-hidden bg-muted border-2 border-border shadow-lg">
                <img 
                  src={ticket.image_url || `https://walottery.com/scratch/assets/imgs/tickets/grid/${ticket.game_id}.jpg`}
                  alt={ticket.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="180" height="240" viewBox="0 0 180 240"><rect fill="%23374151" width="180" height="240"/><text x="50%" y="50%" font-family="system-ui" font-size="14" fill="%239CA3AF" text-anchor="middle" dy=".3em">No Image</text></svg>';
                  }}
                />
              </div>
            </div>
            
            {/* Stats Grid - 2 columns, 2 rows */}
            <StatCard
              icon={DollarSign}
              title="Expected Value"
              value={`$${(ticket.adjusted_ev ?? ticket.ev ?? 0).toFixed(2)}`}
              subtitle={`${ticket.ev_percent?.toFixed(1) || '0'}% return on investment`}
            />
            <StatCard
              icon={Percent}
              title="EV Efficiency"
              value={`${ticket.ev_percent?.toFixed(1) || '0'}%`}
              subtitle={ticket.ev_percent >= 50 ? 'Above average value' : 'Below average value'}
              className={ticket.ev_percent >= 50 ? 'border-success/50' : ''}
            />
            <StatCard
              icon={Trophy}
              title="Top Prize"
              value={ticket.top_prize_amount ? `$${(ticket.top_prize_amount / 1000).toFixed(0)}K` : 'N/A'}
              subtitle={ticket.top_prize_remaining ? `${ticket.top_prize_remaining} remaining` : 'All claimed'}
            />
            <StatCard
              icon={Ticket}
              title="Estimated Tickets Left"
              value={ticket.estimated_tickets_remaining ? `${(ticket.estimated_tickets_remaining / 1000).toFixed(0)}K` : 'Unknown'}
              subtitle={"Based on prize depletion"}
            />
          </div>
          
          {/* Complete Prize Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Complete Prize Breakdown</CardTitle>
              <p className="text-sm text-muted-foreground">All {sortedPrizeTiers.length} prize tiers with odds comparison</p>
            </CardHeader>
            <CardContent>
              <div className="border border-border rounded-lg overflow-hidden overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead>Prize</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Left</TableHead>
                      <TableHead className="text-right">Original Odds</TableHead>
                      <TableHead className="text-right">Current Odds</TableHead>
                      <TableHead>Availability</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPrizeTiers.map((tier, idx) => {
                      const percentRemaining = tier.total_prizes > 0 
                        ? (tier.remaining_prizes / tier.total_prizes) * 100 
                        : 0;
                      
                      return (
                        <TableRow key={idx}>
                          <TableCell className="font-semibold font-mono">
                            ${tier.prize_amount >= 1000 
                              ? `${(tier.prize_amount / 1000).toFixed(0)}K` 
                              : tier.prize_amount.toFixed(0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {tier.total_prizes.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold text-xs">
                            {tier.remaining_prizes.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">
                            {tier.odds || 'N/A'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs font-semibold text-accent">
                            {tier.current_odds || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Progress value={percentRemaining} className="w-12 h-2" />
                              <span className="text-xs font-mono text-muted-foreground">
                                {percentRemaining.toFixed(0)}%
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          
          {/* Prize Depletion Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Overall Prize Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Prizes Remaining</span>
                <span className="font-semibold font-mono">{prizesRemaining.toFixed(1)}%</span>
              </div>
              <Progress 
                value={prizesRemaining} 
                className="h-3"
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Heavily depleted</span>
                <span>Fully stocked</span>
              </div>
              
              {ticket.upper_tier_count > 0 && (
                <div className="mt-4 p-3 bg-accent/5 border border-accent/20 rounded-md">
                  <div className="text-sm font-medium text-accent mb-1">
                    {ticket.upper_tier_count} upper-tier prizes remaining ($100+)
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Upper-tier EV contribution: ${ticket.upper_tier_ev?.toFixed(2) || '0.00'}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Recommendation */}
          <Card className="bg-accent/5 border-accent/50">
            <CardContent className="p-5">
              <div className="text-sm font-medium mb-2 text-accent">AI Recommendation</div>
              <p className="text-sm text-muted-foreground">
                {ticket.ev_percent >= 60 && prizesRemaining >= 50 ?
                  `🎯 Strong buy: High ROI (${ticket.ev_percent?.toFixed(1)}%) with good prize availability (${prizesRemaining.toFixed(0)}% remaining). This ticket offers excellent value.` :
                  ticket.ev_percent >= 40 ?
                  `⚠️ Moderate opportunity: Decent ROI (${ticket.ev_percent?.toFixed(1)}%) but monitor prize depletion (${prizesRemaining.toFixed(0)}% remaining). Consider buying soon.` :
                  ticket.ev_percent > 0 ?
                  `❌ Low priority: Below-average ROI (${ticket.ev_percent?.toFixed(1)}%). Consider higher-ranked tickets for better value.` :
                  `🚫 Avoid: No prizes remaining or insufficient data.`
                }
              </p>
            </CardContent>
          </Card>
          
          {/* Additional Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Technical Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Game Age:</span>
                <span className="font-mono">{ticket.game_age_days || 'Unknown'} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Tickets Printed:</span>
                <span className="font-mono">{ticket.total_tickets_printed?.toLocaleString() || ticket.total_tickets?.toLocaleString() || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Remaining Tickets:</span>
                <span className="font-mono">{ticket.remaining_tickets?.toLocaleString() || ticket.estimated_tickets_remaining?.toLocaleString() || 'N/A'}</span>
              </div>
              
              {/* Overall Odds Comparison */}
              <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-border">
                <div className="text-xs font-medium text-muted-foreground mb-2">OVERALL ODDS (Any Prize)</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground">Original</div>
                    <div className="font-mono font-semibold">{ticket.overall_odds || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Current</div>
                    <div className="font-mono font-semibold text-accent">{ticket.current_overall_odds || 'N/A'}</div>
                  </div>
                </div>
                {ticket.current_overall_odds_value && ticket.overall_odds && (
                  <div className="mt-2 text-xs">
                    <span className="text-muted-foreground">Trend: </span>
                    <span className={`font-semibold ${
                      ticket.odds_trend === 'improving' ? 'text-green-500' : 
                      ticket.odds_trend === 'worsening' ? 'text-red-500' : 
                      'text-yellow-500'
                    }`}>
                      {ticket.odds_trend === 'improving' ? '📈 Improving (better for players)' : 
                       ticket.odds_trend === 'worsening' ? '📉 Worsening (more tickets sold than prizes claimed)' : 
                       '➡️ Stable'}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Breakeven Odds:</span>
                <span className="font-mono">{ticket.breakeven_odds || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Raw EV:</span>
                <span className="font-mono">${ticket.raw_ev?.toFixed(2) || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Adjusted EV:</span>
                <span className="font-mono">${ticket.adjusted_ev?.toFixed(2) || 'N/A'}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
};
